import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import kebabCase from 'lodash/kebabCase';

import useOnScrollBeyondElement from 'utils/hooks/useOnScrollBeyondElement';

const StickyHeadersContext = createContext({ initialized: false });

const useStickyHeaders = () => useContext(StickyHeadersContext);

const StickyHeadersProvider = ({ name, scrollableContainerRef, renderHeaderWrapper, children }) => {
  const wrapperId = kebabCase(`sticky-headers-${name}`);
  const wrapperRef = useRef();
  const [headers, setHeaders] = useState({});

  const activeStickyHeaders = useMemo(
    () =>
      Object.values(headers).reduce((acc, values) => {
        if (values.length > 0) {
          acc.push(values[values.length - 1]);
        }

        return acc;
      }, []),
    [headers],
  );

  const hasStickyHeaders = activeStickyHeaders.length > 0;

  const ifHeaderIsSticky = useCallback(
    headerName => {
      if (!hasStickyHeaders) {
        return false;
      }

      return activeStickyHeaders.includes(headerName);
    },
    [hasStickyHeaders, activeStickyHeaders],
  );

  const value = useMemo(
    () => ({
      name,
      initialized: true,
      wrapperId,
      wrapperRef,
      scrollableContainerRef,
      headers,
      setHeaders,
      activeStickyHeaders,
      hasStickyHeaders,
      renderHeaderWrapper,
      ifHeaderIsSticky,
    }),
    [
      name,
      scrollableContainerRef,
      headers,
      wrapperId,
      renderHeaderWrapper,
      ifHeaderIsSticky,
      activeStickyHeaders,
      hasStickyHeaders,
    ],
  );

  if (!name) {
    return children;
  }

  return <StickyHeadersContext.Provider value={value}>{children}</StickyHeadersContext.Provider>;
};

const StickyHeadersContainer = () => {
  const {
    initialized,
    wrapperId,
    wrapperRef,
    renderHeaderWrapper,
    hasStickyHeaders,
    activeStickyHeaders,
    ifHeaderIsSticky,
  } = useStickyHeaders();

  if (!initialized) {
    return null;
  }

  if (typeof renderHeaderWrapper === 'function') {
    return renderHeaderWrapper({
      children: <div ref={wrapperRef} id={wrapperId} />,
      hasStickyHeaders,
      ifHeaderIsSticky,
      activeStickyHeaders,
    });
  }

  return <div ref={wrapperRef} id={wrapperId} />;
};

const StickyContentMarker = ({
  name,
  stackIdentifier = 'default',
  className,
  topPositionPx = 0,
  enabled = true,
  onScrolledBeyond,
  renderHeader,
  ...props
} = {}) => {
  const { initialized, scrollableContainerRef, wrapperRef, setHeaders, headers, ifHeaderIsSticky } = useStickyHeaders();

  const isValidStickyHeader = initialized && name && enabled && typeof renderHeader === 'function';

  const handleScrolledBeyond = useCallback(
    ({ isBeyond }) => {
      if (isValidStickyHeader) {
        setHeaders(prev => {
          let stackHeaders = prev[stackIdentifier] || [];

          if (isBeyond) {
            stackHeaders = [...stackHeaders, name];
          } else {
            stackHeaders = stackHeaders.filter(header => header !== name);
          }

          return {
            ...prev,
            [stackIdentifier]: stackHeaders,
          };
        });
      }

      if (typeof onScrolledBeyond === 'function') {
        onScrolledBeyond({ isBeyond });
      }
    },
    [name, stackIdentifier, setHeaders, onScrolledBeyond, isValidStickyHeader],
  );

  const { ref, isScrolledBeyond } = useOnScrollBeyondElement({
    containerRef: scrollableContainerRef,
    topPosition: topPositionPx,
    enabled: isValidStickyHeader,
    onScrolledBeyond: handleScrolledBeyond,
  });

  const shouldRenderStickyHeader = useMemo(() => {
    if (!isValidStickyHeader || !isScrolledBeyond) {
      return false;
    }

    const stackedHeaders = headers[stackIdentifier] || [];
    const lastHeader = stackedHeaders[stackedHeaders.length - 1];

    return lastHeader === name;
  }, [isValidStickyHeader, isScrolledBeyond, headers, name, stackIdentifier]);

  return (
    <div ref={ref} {...(className && { className })}>
      {shouldRenderStickyHeader &&
        createPortal(renderHeader({ isScrolledBeyond, ifHeaderIsSticky, props }), wrapperRef?.current)}
    </div>
  );
};

const StickyHeaderState = ({ children }) => {
  const { hasStickyHeaders, ifHeaderIsSticky } = useStickyHeaders();

  if (typeof children === 'function') {
    return children({ hasStickyHeaders, ifHeaderIsSticky });
  }

  return children;
};

export { StickyHeadersProvider, StickyHeadersContainer, StickyHeaderState };
export default StickyContentMarker;
